/**
 * 案件・承認・確定（F4）のルートパス定義。案件から見た業務フローのパスは `flow.ts` が持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧・案件の変更一覧・承認一覧のルートパスを定義する。
 * @graph-connects none
 */
import type { FeatureRouteSets } from './feature-route-sets.js';

/** PJ の一覧。差分を見たい PJ をここから選ぶ。 @graph-connects none */
export const PJS_ROUTE = '/pjs';

/**
 * 承認一覧。未確定の全 PJ の承認事項（承認が要る型の変更）を
 * 1 表に並べ、担当者が判断する。
 *
 * PJ 単位の画面（PJ サマリー）に承認の節を置く形から、PJ を跨ぐ別画面へ移した。承認する
 * 担当者は「この PJ の提案は通してよいか」ではなく「同じ語や分類を別の PJ が別の意味で提案して
 * いないか」を見て判断するので、PJ を 1 つずつ開く形では判断の材料が揃わない。
 *
 * PJ を検索条件（`?pj=`）で受けない。この画面は PJ を適用した姿を描くのではなく、全 PJ を
 * 横断して並べる画面なので、PJ モードの現在地（`PJ_MODE_ROUTES`）には入れない。
 * @graph-connects none
 */
export const APPROVALS_ROUTE = '/approvals';

/**
 * PJ をパスに置くルートで、PJ 番号が入るパスパラメータの名前。
 *
 * PJ は URL の 2 通りの場所に現れる — 一覧・詳細では検索条件（`?pj=`）、PJ サマリーと
 * PJ から見たフローではパスの親（`/pjs/$pjNumber/...`）である。**「いま見ている PJ」を
 * 読む側は、その 2 通りを 1 つの答えに畳む必要がある**（`currentPjOf`）。畳む側がこの名前を
 * 直に書くと、ルートの綴りを変えたときに黙って読めなくなる。
 *
 * ルートの雛形は TanStack Router が型のためにリテラルを要求するので、この定数から組み立てる
 * ことはできない。代わりに、雛形がこの名前で終わることをテストで突き合わせる。
 * @graph-connects none
 */
export const PJ_PATH_PARAM = 'pjNumber';

/**
 * ④PJ の変更一覧 — 1 件の案件が仕様のどこを変えたかを、要求 │ ステージ │ 業務フロー │ 業務 │
 * 業務ルール の 5 レーンで辿る（要素表 `spec_graph_*` を読む）。
 *
 * **名前は DIFF だが、指すのは変更一覧である。** 欄ごとの差分は `PJ_FIELD_DIFF_ROUTE` が持つ。
 * この名前を変えると、案件から見た業務フロー・案件モードを外す導線（F2 の持ち物）まで波及する。
 *
 * PJ 番号をパスに置くのは、担当者が自分の案件の変更一覧をそのまま共有・再訪できるように
 * するためである。絞り込み条件をクエリに置くと、共有したリンクが「絞り込み前の一覧」に
 * 見えてしまう。
 *
 * **別のパスを足さず、このパスの中身を入れ替えた。** 作り直しのビューを `/pjs/$pjNumber/lanes`
 * として足していた期間は終わり、案件の読取（`listPjs` / `getChangeHistory` /
 * `listPjRequirements`）が揃ったので旧表を読む中身と差し替えた。URL は 1 文字も
 * 変わらないので、共有済みのリンクはそのまま新しい中身へ着く。
 * @graph-connects none
 */
export const PJ_DIFF_ROUTE = '/pjs/$pjNumber';

/**
 * PJ の差分 — 案件が変えた要素ごとに、欄の変更前（公開中の仕様）と変更後を 3 列で並べる。
 *
 * 画面から来る入口は無い（入口だった変更一覧の帯の「差分を見る」は #4807 で外した）。URL を直に
 * 開くと来られる。探す画面（一覧・変更一覧）は印だけを出し、欄の単位の見比べはこの画面 1 枚に
 * 集める（案件モードの原則）。
 *
 * **案件をパスに置くルートの集合（`pjPath`）には入れない。** 入れると案件モードを外す導線
 * （`pjExitTarget`）がこの画面を「検索条件を落とす画面」として扱い、外しても同じ画面に留まる。
 * 行き先を決める判断は F2 の持ち物なので、そちらが差分を扱うまでは集合の外に置く。
 * @graph-connects none
 */
export const PJ_FIELD_DIFF_ROUTE = '/pjs/$pjNumber/diff';

/**
 * 案件・承認のルートの集合。どれも検索条件で案件を受けない（案件の一覧と承認一覧は案件を
 * 適用した姿ではなく、変更一覧と差分は案件をパスの親に置く）。
 * @graph-connects none
 */
export const pjRouteSets = {
  all: [PJS_ROUTE, APPROVALS_ROUTE, PJ_DIFF_ROUTE, PJ_FIELD_DIFF_ROUTE],
  pjSearch: [],
  operationSearch: [],
  pjPath: [PJ_DIFF_ROUTE],
} as const satisfies FeatureRouteSets;
